import { statusFormProps } from "../../../shared/statusFormProps";
import { useMutation } from "@tanstack/react-query";
import { useState, type FC } from "react";
import Stack from "@mui/material/Stack";
import { SharedProcessingAlert } from "../../../enterpriseCustomer/SharedProcessingAlert";
import { SharedProcessingWarning } from "../../../enterpriseCustomer/SharedProcessingWarning";
import Box from "@mui/material/Box";
import { Alert } from "@privacy/ui";
import { useTranslation } from "@privacy/translations";
import { Drawer } from "../../../shared/Drawer";
import { DynamicForm } from "../../../dynamicForm";
import {
  prefetchDpiaRecommendedForm,
  setConnectedTenantsMutationOptions,
  prefetchRegistrationUsersWithAccess,
  useGetRegistrationById,
  useGetRegistrationForm,
  useGetRegistrationUsersWithAccessCount,
  useSetResponsible,
  useUpdateRegistrationAnswers,
  useUpdateRegistrationStatus,
} from "../../../api/registrations";
import { useRegistrationVariant } from "../RegistrationVariantProvider";
import { processorOf, type RegistrationById } from "../../../api/registrations.types";
import { usePermission } from "../../../shared/permissions";
import { PersonIcon1, DpiaIcon1 } from "../../../shared/icons";
import { usePrivacyNavigate } from "../../../shared/usePrivacyNavigate";
import { DynamicGrid } from "../../../dynamicGrid";
import { RegistrationStatusSelect } from "../RegistrationStatusSelect";
import { trackEvent } from "../../../shared/Snowplow";
import { RegistrationDrawerHeader } from "./RegistrationDrawerHeader";
import { ConnectedTenants } from "./ConnectedTenants";
import { ConnectedProcessor } from "./ConnectedProcessor";
import { useCurrentCustomer } from "../../../root/CurrentOrganisationProvider";
import { RegistrationResponsible } from "./RegistrationResponsible";
import { DatesBand } from "../../../shared/DateTimeInfo";
import {
  useHasPermissionToChooseResponsible,
  useHasPermissionToUpdateAnswers,
  useHasPermissionToUpdateMetaData,
} from "./useHasPermissionToUpdate";
import { RegistrationMinorOrMajorChangeDialog } from "./RegistrationMinorOrMajorChangeDialog";
import { DpiaRecommendedSubDrawerTitle } from "./DpiaRecommendedSubDrawer";

const REGISTRATION_FILE_OWNER_TYPES = {
  Controller: "Registration",
  Processor: "ProcessorRegistration",
} as const;

export const RegistrationInlineFormDrawer = () => {
  const registrationId = DynamicGrid.useSelectedRowId();
  const { navigate } = usePrivacyNavigate();
  const { t } = useTranslation();
  const variant = useRegistrationVariant();
  const currentCustomer = useCurrentCustomer();
  const registrationByIdQuery = useGetRegistrationById(registrationId, variant);
  const usersWithAccessCountQuery = useGetRegistrationUsersWithAccessCount(registrationId, variant);
  const hasPermissionToUpdateAnswers = useHasPermissionToUpdateAnswers();
  const hasPermissionToUpdateMetaData = useHasPermissionToUpdateMetaData();
  const hasPermissionToChooseResponsible = useHasPermissionToChooseResponsible();
  const registrationAnswersMutation = useUpdateRegistrationAnswers();
  const setResponsibleMutation = useSetResponsible(registrationId);
  const setConnectedTenantsMutation = useMutation(setConnectedTenantsMutationOptions(variant));
  const registrationsFormQuery = useGetRegistrationForm(variant);
  const updateStatusMutation = useUpdateRegistrationStatus(registrationId);
  const [isMajorOrMinorChangeDialogOpen, setIsMajorOrMinorChangeDialogOpen] = useState(false);

  return (
    <DynamicGrid.Drawer>
      {(() => {
        if (!registrationByIdQuery.data || !registrationsFormQuery.data) return <Drawer.Loading />;

        const registration = registrationByIdQuery.data;
        return (
          <>
            <DynamicForm.Provider
              key={registration.id}
              fileOwner={{ type: REGISTRATION_FILE_OWNER_TYPES[variant], id: registration.id }}
              onSave={answer => {
                if (registration.status.status === "done") setIsMajorOrMinorChangeDialogOpen(true);
                return registrationAnswersMutation.mutateAsync(
                  { registrationId: registration.id, answers: [answer] },
                  { onError: () => setIsMajorOrMinorChangeDialogOpen(false) }
                );
              }}
              answers={registration.answers}
              isReadOnly={!hasPermissionToUpdateAnswers(registration)}
              isSaveError={
                registrationAnswersMutation.isError ||
                setResponsibleMutation.isError ||
                setConnectedTenantsMutation.isError
              }
              isSaving={
                registrationAnswersMutation.isPending ||
                setResponsibleMutation.isPending ||
                setConnectedTenantsMutation.isPending
              }
              {...statusFormProps(
                registration,
                updateStatusMutation.error,
                t("RegistrationDrawer.UnansweredMandatoryQuestion.Input")
              )}
            >
              {({ formState }) => (
                <>
                  <RegistrationDrawerHeader
                    activeTab="standard"
                    registration={registration}
                    statusIndicator={DynamicForm.formStateToDrawerHeaderStatus(formState)}
                    bottomSlot={
                      <DatesBand
                        createdAt={registration.createdAt}
                        updatedAt={registration.updatedAt}
                        action={
                          <RegistrationStatusSelect
                            registration={registration}
                            updateStatusMutation={updateStatusMutation}
                          />
                        }
                      >
                        <RegistrationAlert registration={registration} />
                      </DatesBand>
                    }
                  />
                  <Drawer.Content sx={{ pt: 3 }}>
                    <SharedProcessingAlert registration={registration} sx={{ mb: 3 }} />
                    <Stack sx={{ mb: 5 }}>
                      {variant === "Processor" && currentCustomer.customerKind !== "enterprise" && (
                        <ConnectedProcessor name={processorOf(registration)?.name} />
                      )}
                      <ConnectedTenants
                        isReadOnly={!hasPermissionToUpdateMetaData(registration)}
                        registration={registration}
                        setConnectedTenantsMutation={setConnectedTenantsMutation}
                      />
                      <RegistrationResponsible
                        isReadOnly={!hasPermissionToChooseResponsible(registration)}
                        registration={registration}
                        setResponsibleMutation={setResponsibleMutation}
                      />
                      <Drawer.GoToSubDrawerButtonsWrapper>
                        <Drawer.GoToSubDrawerButton
                          Icon={PersonIcon1}
                          label={t("RegistrationDrawer.UsersWithAccessButton")}
                          count={usersWithAccessCountQuery.data?.numberOfUsersWithAccess}
                          onClick={() => navigate("users-with-access")}
                          onMouseOver={() => prefetchRegistrationUsersWithAccess(registrationId, variant)}
                        />
                        {variant === "Controller" && (
                          <Drawer.GoToSubDrawerButton
                            Icon={DpiaIcon1}
                            label={
                              <DpiaRecommendedSubDrawerTitle
                                sx={theme => ({ fontSize: theme.typography.pxToRem(14) })}
                                autoDPIAPreAssessment={registration.autoDPIAPreAssessment}
                              />
                            }
                            onMouseOver={() => prefetchDpiaRecommendedForm(variant)}
                            onClick={() => navigate("dpia-recommended")}
                          />
                        )}
                      </Drawer.GoToSubDrawerButtonsWrapper>
                    </Stack>

                    <Drawer.Divider />
                    <Stack sx={{ my: 5 }}>
                      <DynamicForm.InlineForm id={registration.id} form={registrationsFormQuery.data} />
                    </Stack>
                  </Drawer.Content>
                </>
              )}
            </DynamicForm.Provider>
            {isMajorOrMinorChangeDialogOpen && (
              <RegistrationMinorOrMajorChangeDialog
                answerRequestMutation={registrationAnswersMutation}
                onClose={() => setIsMajorOrMinorChangeDialogOpen(false)}
                registrationId={registration.id}
              />
            )}
          </>
        );
      })()}
    </DynamicGrid.Drawer>
  );
};

export const RegistrationMultiStepFormDrawer = () => {
  const registrationId = DynamicGrid.useSelectedRowId();
  const { t } = useTranslation();
  const variant = useRegistrationVariant();
  const registrationByIdQuery = useGetRegistrationById(registrationId, variant);
  const hasPermissionToUpdateAnswers = useHasPermissionToUpdateAnswers();
  const registrationsFormQuery = useGetRegistrationForm(variant);
  const registrationAnswersMutation = useUpdateRegistrationAnswers();
  const updateStatusMutation = useUpdateRegistrationStatus(registrationId);
  const [isReviewOrDoneDialogOpen, setIsReviewOrDoneDialogOpen] = useState(false);

  return (
    <DynamicForm.MultiStepFormDialog>
      {(() => {
        if (!registrationByIdQuery.data || !registrationsFormQuery.data) return <Drawer.Loading />;

        const registration = registrationByIdQuery.data;
        return (
          <>
            <DynamicForm.Provider
              key={registration.id}
              fileOwner={{ type: REGISTRATION_FILE_OWNER_TYPES[variant], id: registration.id }}
              onSave={answer => {
                trackEvent.answerQuestionRegistrationGuidedView(answer.type);
                if (registration.status.status === "done") setIsReviewOrDoneDialogOpen(true);
                return registrationAnswersMutation.mutateAsync(
                  { registrationId: registration.id, answers: [answer] },
                  { onError: () => setIsReviewOrDoneDialogOpen(false) }
                );
              }}
              answers={registration.answers}
              isReadOnly={!hasPermissionToUpdateAnswers(registration)}
              isSaveError={registrationAnswersMutation.isError}
              isSaving={registrationAnswersMutation.isPending}
              {...statusFormProps(
                registration,
                updateStatusMutation.error,
                t("RegistrationDrawer.UnansweredMandatoryQuestion.Input")
              )}
            >
              {({ formState }) => (
                <DynamicForm.Form
                  header={
                    <RegistrationDrawerHeader
                      activeTab="guided"
                      registration={registration}
                      topRightSlot={
                        <RegistrationStatusSelect
                          registration={registration}
                          updateStatusMutation={updateStatusMutation}
                        />
                      }
                      bottomSlot={
                        <Box sx={{ pb: 3, pt: 1 }}>
                          <RegistrationAlert registration={registration} />
                        </Box>
                      }
                      statusIndicator={DynamicForm.formStateToDrawerHeaderStatus(formState)}
                    />
                  }
                  form={registrationsFormQuery.data}
                />
              )}
            </DynamicForm.Provider>
            {isReviewOrDoneDialogOpen && (
              <RegistrationMinorOrMajorChangeDialog
                answerRequestMutation={registrationAnswersMutation}
                registrationId={registration.id}
                onClose={() => setIsReviewOrDoneDialogOpen(false)}
              />
            )}
          </>
        );
      })()}
    </DynamicForm.MultiStepFormDialog>
  );
};

const RegistrationAlert: FC<{ registration: RegistrationById }> = ({ registration }) => {
  const { t } = useTranslation();
  const { hasPermission } = usePermission();
  const hasReviewPermission = hasPermission("nova-forms-api:registration:review", registration.permissions);
  if (registration.status.status === "review" && registration.nobodyCanReview) {
    return <SharedProcessingWarning>{t("RegistrationGrid.Drawer.NobodyCanReview")}</SharedProcessingWarning>;
  }
  if (
    registration.status.status === "review" &&
    !hasReviewPermission &&
    !(registration.ownCopy && !registration.responsible)
  ) {
    return (
      <Alert sx={{ padding: "3px 16px" }} severity="info">
        {t("RegistrationGrid.Drawer.InfoAlert.Review")}
      </Alert>
    );
  }
  return null;
};
